import { useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Group, Button, Text, Stack } from '@repletoryreach/sdk-ui';
import { NumberInput, Switch } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import type { admin } from '@repletoryreach/sdk-api';
import { usePriceLadder, useUpdateSkuRate } from '../../../../data/useSkuRates';
import { formatInteger, formatMinor } from '../../../../lib/format';
import { chargeInForce, floorMinor, ladderRefusal, planAmounts } from '../../../../lib/priceLadder';
import { useT } from '../../../../i18n/I18nProvider';
import { PlanPrices } from './PlanPrices';

type Sku = NonNullable<Awaited<ReturnType<typeof admin.listSkuRates>>['data']>[number];

/**
 * The shape this component needs out of a cached ledger query. Read
 * structurally rather than through the SDK type because it is only ever a cache
 * peek: nothing is fetched here, and an absent cache is a normal case.
 */
interface CachedLedger {
  data?: Array<{ sku_code: string | null; organisation_id: string; occurred_at: string }>;
}

/**
 * The largest ledger response already in the cache, whichever page put it
 * there. The ledger and usage pages key their queries by org, limit and range,
 * so a single fixed key would miss them all.
 */
function cachedLedger(qc: QueryClient): CachedLedger | undefined {
  let best: CachedLedger | undefined;
  for (const [, data] of qc.getQueriesData<CachedLedger>({ queryKey: ['admin', 'ledger'] })) {
    const rows = data?.data;
    if (!rows?.length) continue;
    if (!best?.data || rows.length > best.data.length) best = data;
  }
  return best;
}

const SEVEN_DAYS_MS = 7 * 86_400_000;

/**
 * How much of the platform rides on this SKU, from whatever ledger rows are
 * already in the cache. Returns null when nothing has been loaded, so the
 * modal can stay silent rather than claim a confident zero.
 */
function blastRadius(cached: CachedLedger | undefined, sku: string): { events: number; orgs: number } | null {
  const rows = cached?.data;
  if (!rows?.length) return null;
  const since = Date.now() - SEVEN_DAYS_MS;
  const orgs = new Set<string>();
  let events = 0;
  for (const row of rows) {
    if (row.sku_code !== sku) continue;
    if (new Date(row.occurred_at).getTime() < since) continue;
    events += 1;
    orgs.add(row.organisation_id);
  }
  return { events, orgs: orgs.size };
}

/**
 * Margin on a charge, as "R0,10 (11.5%)". For a charge that is still being
 * typed and has no row yet. A row of the price book prints the service's own
 * figures instead: see {@link rateMarginText}.
 */
export function marginText(charge: number, cost: number, currency: string): string {
  const margin = charge - cost;
  const pct = charge > 0 ? Math.round(((margin * 1000) / charge)) / 10 : null;
  return pct == null ? formatMinor(margin, currency) : `${formatMinor(margin, currency)} (${pct}%)`;
}

/**
 * The margin on a price book row, as the service worked it out. It is on the
 * list price in force, which is never under the floor, so it is printed as it
 * comes and never recomputed from the stored charge.
 */
export function rateMarginText(rate: Sku): string {
  const margin = formatMinor(rate.margin_minor, rate.currency);
  return rate.margin_percent == null ? margin : `${margin} (${rate.margin_percent}%)`;
}

// The fields work in major units (0.87 is 87 cents): the way a price list is
// read off a Meta or Bedrock invoice. Minor units are what the API stores.
const toMajor = (minor: number) => Math.round(minor) / 100;
const toMinorUnits = (major: number | '') => (major === '' ? '' : Math.round(major * 100));

/**
 * The one mutation in this console with platform-wide financial consequences.
 * The modal is the confirmation: it names the SKU, shows the margin the new
 * charge leaves, states that every tenant pays it from today, and its button
 * says what it does. No second dialog on top.
 *
 * No rate is listed under its floor, the cost plus the free plan's markup
 * (owner's ruling, 21 September 2026). A rate that follows the price ladder is
 * priced at that floor by the service, so the charge field shows the floor for
 * the cost typed and cannot be edited. A charge set by hand has to clear it.
 */
export function RateVersionModal({ rate, opened, onClose }: { rate: Sku; opened: boolean; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const mut = useUpdateSkuRate();
  const ladder = usePriceLadder();
  const { id, sku_code: sku, cost_minor: cost, currency } = rate;
  // What tenants pay today, which is what the dialog and the toast quote.
  const value = chargeInForce(rate);
  const [draft, setDraft] = useState<number | ''>(toMajor(value));
  const [draftCost, setDraftCost] = useState<number | ''>(toMajor(cost));
  const [markup, setMarkup] = useState<number | ''>('');
  // On for a row that sits at its floor today. A charge an operator set above
  // the floor opens as it stands, so opening the dialog never offers to cut it.
  const [follow, setFollow] = useState(rate.follows_ladder ?? cost > 0);
  const [refusal, setRefusal] = useState<string | null>(null);

  const previewCost = typeof draftCost === 'number' ? (toMinorUnits(draftCost) as number) : cost;
  // Null until the plans have been read: the free plan's markup sets the floor.
  const entryMarkup = ladder.entryMarkupPercent;
  const floor = entryMarkup == null ? null : floorMinor(previewCost, entryMarkup);
  // A unit that costs nothing has no floor to follow, and following it would price the unit at nothing.
  const canFollow = previewCost > 0;
  const following = follow && canFollow;
  const typedCharge = typeof draft === 'number' ? (toMinorUnits(draft) as number) : null;
  const previewCharge = following ? floor : typedCharge;
  const belowFloor = !following && floor != null && typedCharge != null && typedCharge < floor;
  const unchanged = previewCharge === value && previewCost === cost;
  const impact = opened ? blastRadius(cachedLedger(qc), sku) : null;

  /** Charge = cost × (1 + markup): the way a price list is usually set. */
  function applyMarkup() {
    if (typeof markup !== 'number' || typeof draftCost !== 'number') return;
    const charge = Math.round(draftCost * (1 + markup / 100) * 100);
    // The floor rounds up to the cent. A markup at or over the free plan's is
    // meant to clear it, so it is not left a cent short by rounding down.
    const lifted = floor != null && entryMarkup != null && markup >= entryMarkup ? Math.max(charge, floor) : charge;
    setDraft(lifted / 100);
    setRefusal(null);
  }

  function toggleFollow(on: boolean) {
    setFollow(on);
    setRefusal(null);
    // Handing the charge back starts from a figure that can be saved.
    if (!on && floor != null && (typedCharge == null || typedCharge < floor)) setDraft(toMajor(floor));
  }

  async function save() {
    if (previewCharge == null || belowFloor) return;
    setRefusal(null);
    try {
      const res = await mut.mutateAsync({ id, amount_minor: previewCharge, cost_minor: previewCost, ...(following ? { follow_ladder: true } : {}) });
      // The service answers with the version it wrote. On the ladder the
      // service sets the charge, so its figure is the one to quote.
      const written = res.data?.amount_minor ?? previewCharge;
      notifySuccess(
        t('billing.skuRates.toast.updated', { sku, from: formatMinor(value, currency), to: formatMinor(written, currency) }),
        t('billing.skuRates.toast.updatedTitle')
      );
      onClose();
    } catch (e) {
      const refused = ladderRefusal(e);
      if (refused?.code === 'RATE_BELOW_FLOOR' && refused.floor_minor != null) {
        setRefusal(t('billing.skuRates.edit.refusal.belowFloor', {
          cost: formatMinor(refused.cost_minor ?? previewCost, currency),
          floor: formatMinor(refused.floor_minor, currency),
          pct: refused.entry_markup_percent ?? entryMarkup ?? ''
        }));
        // The service's floor is not the one shown, so the free plan's markup has moved since it was read.
        void ladder.refetch();
        return;
      }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.skuRates.toast.updateFailed'));
    }
  }

  const chargeHint = following
    ? entryMarkup == null ? t('billing.skuRates.edit.ladderChargeUnknown') : t('billing.skuRates.edit.ladderChargeHint', { pct: entryMarkup })
    : floor != null && floor > 0 && entryMarkup != null
      ? t('billing.skuRates.edit.floorHint', { floor: formatMinor(floor, currency), pct: entryMarkup })
      : t('billing.skuRates.edit.amountHint', { example: formatMinor(87, currency) });
  const chargeError = refusal
    ?? (belowFloor && floor != null && typedCharge != null
      ? t('billing.skuRates.edit.belowFloor', { charge: formatMinor(typedCharge, currency), floor: formatMinor(floor, currency) })
      : undefined);

  return (
    <Modal opened={opened} onClose={onClose} title={t('billing.skuRates.modal.title', { sku })} size="lg">
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {rate.name} · {t(`billing.skuRates.unit.${rate.unit}`, rate.unit)} · {t('billing.skuRates.modal.current', { charge: formatMinor(value, currency), cost: formatMinor(cost, currency) })}
        </Text>
        {ladder.isError && (
          <Alert color="warning">
            <Group justify="space-between" gap="sm" wrap="wrap">
              <Text role="bodySmall">{t('billing.skuRates.edit.plansError')}</Text>
              <Button size="xs" variant="outline" onClick={() => void ladder.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}
        <Switch
          checked={following}
          disabled={!canFollow}
          onChange={toggleFollow}
          label={t('billing.skuRates.edit.follow')}
          description={canFollow ? t('billing.skuRates.edit.followHint') : t('billing.skuRates.edit.followNoCost')}
        />
        <Group gap="sm" grow align="flex-start">
          <NumberInput
            value={draftCost}
            onChange={(v) => { setDraftCost(typeof v === 'number' ? v : ''); setRefusal(null); }}
            min={0}
            step={0.01}
            label={t('billing.skuRates.edit.costLabel', { currency })}
            description={t('billing.skuRates.edit.costHint')}
          />
          <NumberInput
            value={markup}
            onChange={(v) => setMarkup(typeof v === 'number' ? v : '')}
            min={0}
            // On the ladder the free plan's markup sets the charge, not this
            // one. The steppers go too: they are buttons of their own and a
            // disabled field does not stop them.
            disabled={following}
            hideControls={following}
            label={t('billing.skuRates.edit.markupLabel')}
            description={t('billing.skuRates.edit.markupHint')}
            onBlur={applyMarkup}
          />
          <NumberInput
            // On the ladder the field shows the floor for the cost typed, and
            // keeps what was typed for when the charge is handed back.
            value={following ? (floor == null ? '' : toMajor(floor)) : draft}
            // A blank field stays blank. Coercing it to 0 here used to make
            // select-all + delete + save price the SKU free across the platform.
            onChange={(v) => { if (following) return; setDraft(typeof v === 'number' ? v : ''); setRefusal(null); }}
            min={0}
            step={0.01}
            disabled={following}
            hideControls={following}
            label={t('billing.skuRates.edit.amountLabel', { currency })}
            description={chargeHint}
            error={chargeError}
          />
        </Group>
        {previewCharge != null && (
          <Text role="bodySmall" fw={600}>
            {t('billing.skuRates.edit.preview', { charge: formatMinor(previewCharge, currency), cost: formatMinor(previewCost, currency), margin: marginText(previewCharge, previewCost, currency) })}
          </Text>
        )}
        {previewCharge != null && !belowFloor && ladder.plans.length > 0 && (
          <Stack gap="xs">
            <Text role="overline" c="var(--rr-color-text-muted)">{t('billing.skuRates.edit.byPlan')}</Text>
            <PlanPrices amounts={planAmounts(ladder.all, previewCharge, previewCost)} plans={ladder.plans} currency={currency} />
          </Stack>
        )}
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.skuRates.modal.consequence', { currency })}</Text>
        {impact && impact.events > 0 && (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {t('billing.skuRates.modal.confirm.usage', { events: formatInteger(impact.events), orgs: formatInteger(impact.orgs) })}
          </Text>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={mut.isPending} disabled={previewCharge == null || unchanged || belowFloor} onClick={() => void save()}>
            {t('billing.skuRates.modal.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** The trigger on a row. The modal mounts only while open, so each row is cheap. */
export function RateChangeButton({ rate }: { rate: Sku }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="xs" variant="outline" onClick={() => setOpen(true)}>{t('billing.skuRates.action.change')}</Button>
      {open && <RateVersionModal rate={rate} opened={open} onClose={() => setOpen(false)} />}
    </>
  );
}
